import { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  Snackbar,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Close,
  ErrorOutline,
  HelpOutline,
  PhotoCamera,
  RemoveCircleOutline,
  Schedule,
  Search,
  SwapHoriz
} from '@mui/icons-material';
import { Facility } from '../../utils/facilityStorage';
import { User, isFreightrollUser } from '../../utils/roleHelper';
import { UserContext } from '../../App';
import AddTrailerModal, {
  AddTrailerLocation,
  AddTrailerValues
} from 'components/AddTrailerModal';
import { AvailableLocations, loadAvailableLocations } from 'api/availableLocations';
import {
  CreateUnknownTrailerResult,
  GhostTrailerMatch,
  YardObservationDecision,
  fetchYardObservationAuditLog,
  createUnknownTrailer,
  resolveGhostTrailerWithMatch,
  fetchYardObservationExceptions,
  ignoreException,
  TrailerLoadStatus,
  undoAuditAction
} from 'api/yardReconciliation';
import { YardGeometryProps } from 'api/yardGeometry';
import {
  FacilityLocation,
  FacilityLocations,
  loadFacilityLocations
} from 'api/facilityLocations';
import YardSpotMap from './YardSpotMap';
import GhostTrailerMatchDialog, {
  GhostTrailerMatchAction
} from './GhostTrailerMatchDialog';
import {
  AuditLogEntry,
  AuditStep,
  ExecutedStep,
  SpotLabel,
  EXCEPTION_TYPE_LABELS,
  ExceptionSeverity,
  ExceptionType,
  YardException,
  countAutoResolved,
  createTrailerOutcome,
  executedStepOf,
  toAuditEntries,
  toYardException,
  zonedPlaceLabel
} from './reconciliation';

const MONO = "'Roboto Mono', monospace";

// The app's usual button blue (Tailwind blue-500, blue-600 on hover), lighter
// than MUI's default primary.
const BLUE_BUTTON_SX = { 'bgcolor': '#3b82f6', '&:hover': { bgcolor: '#2563eb' } };

const SEVERITY_COLOR: Record<ExceptionSeverity, string> = {
  open: '#1976d2',
  warn: '#ed6c02',
  low: '#d32f2f'
};

const TYPE_ICONS: Record<ExceptionType, typeof SwapHoriz> = {
  staleYmsEmptySpot: RemoveCircleOutline,
  fuzzyMismatch: SwapHoriz,
  ghostTrailer: HelpOutline
};

const STEP_CHIP_SX: Record<ExecutedStep, object | undefined> = {
  Move: { bgcolor: '#e3f2fd', color: '#0d47a1', border: '1px solid #90caf9' },
  Match: { bgcolor: '#e8f5e9', color: '#1b5e20', border: '1px solid #a5d6a7' },
  Ignore: undefined,
  Error: { bgcolor: '#fdecea', color: '#c62828', border: '1px solid #ef9a9a' }
};

// The steps each column's filter offers, in the order it lists them.
const SUGGESTED_STEPS: AuditStep[] = ['Move', 'Match', 'Ignore'];
const EXECUTED_STEPS: ExecutedStep[] = ['Move', 'Match', 'Ignore', 'Error'];

const StepChip = ({ step }: { step: ExecutedStep }) => (
  <Chip
    size="small"
    label={step}
    variant={step === 'Ignore' ? 'outlined' : 'filled'}
    sx={STEP_CHIP_SX[step]}
  />
);

// Wraps an input rendered inside a column header. The grid treats clicks, keys
// and focus inside a header as its own — sorting, keyboard navigation, and
// pulling focus back onto the header cell — so they're kept from reaching it.
const HeaderControl = ({ children }: { children: ReactNode }) => (
  <Box
    onClick={(e) => e.stopPropagation()}
    onKeyDown={(e) => e.stopPropagation()}
    onFocus={(e) => e.stopPropagation()}
    sx={{ width: '100%' }}
  >
    {children}
  </Box>
);

// A column title with no hover tooltip, which the grid otherwise adds to any
// title it has to cut short.
// A second line, when given, wraps the title so the column can stay narrow.
const PlainHeader = ({ title, subtitle }: { title: string; subtitle?: string }) => (
  <Box component="span" sx={{ fontWeight: 500, lineHeight: 1.2, whiteSpace: 'normal' }}>
    {title}
    {subtitle && (
      <>
        <br />
        {subtitle}
      </>
    )}
  </Box>
);

// A spot or door by name; its zone on hover. A dash when there is none.
const SpotCell = ({ spot }: { spot: SpotLabel | null }) =>
  spot ? (
    <Tooltip
      arrow
      placement="top"
      title={
        <>
          <div>Zone: {spot.zone ?? '—'}</div>
          <div>Spot: {spot.name}</div>
        </>
      }
    >
      <Box component="span" sx={{ fontSize: 13 }}>
        {spot.name}
      </Box>
    </Tooltip>
  ) : (
    <Box component="span" sx={{ color: 'text.secondary' }}>
      —
    </Box>
  );

// A column header that filters the log by step; none selected shows every step.
const StepFilter = <Step extends ExecutedStep>({
  label,
  steps,
  value,
  onChange
}: {
  label: string;
  steps: Step[];
  value: Step[];
  onChange: (steps: Step[]) => void;
}) => (
  <HeaderControl>
    <TextField
      select
      fullWidth
      variant="standard"
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value as unknown as Step[])}
      inputProps={{ 'aria-label': `Filter by ${label.toLowerCase()}` }}
      InputProps={{ sx: { fontSize: 13 } }}
      SelectProps={{
        multiple: true,
        displayEmpty: true,
        renderValue: (selected) => {
          const chosen = selected as Step[];
          if (chosen.length === 0) return label;
          return `${label}: ${chosen.join(', ')}`;
        }
      }}
    >
      {steps.map((step) => (
        <MenuItem key={step} value={step} dense>
          <Checkbox size="small" checked={value.includes(step)} />
          <StepChip step={step} />
        </MenuItem>
      ))}
    </TextField>
  </HeaderControl>
);

// Whether a step passes a filter; an empty filter passes every step.
const passes = (filter: ExecutedStep[], step: ExecutedStep | null) =>
  filter.length === 0 || (step !== null && filter.includes(step));

type LogFilter = 'all' | 'manual' | 'auto';

// How far back both the exceptions and the audit log are pulled.
const TIME_WINDOWS = [
  { hours: 1, label: '1 hour' },
  { hours: 24, label: '24 hours' }
] as const;
type TimeWindowHours = (typeof TIME_WINDOWS)[number]['hours'];

const NO_SPOT = '---';

// A yard spot's or dock's name and zone, from flux's yard and warehouse
// layouts — the RTLS side's names, since the row's own rtlsZoneName is only
// ever the rough evidence.fuzzy.zone guess. The YMS side needs none of this:
// current_location carries its own names.
function findLocation(
  locations: FacilityLocations | null,
  uuid: string | null
): FacilityLocation | null {
  if (!locations || !uuid) return null;
  return locations.get(uuid) ?? null;
}

// Any open unknown-trailer exception can create its trailer — flux places it
// at the read's spot, or in its zone for a zone-level read.
function canCreateTrailer(exception: YardException) {
  return Boolean(exception.exceptionId && exception.type === 'ghostTrailer');
}

// Where the Add Trailer modal starts: the dock or yard spot the read saw the
// trailer at, by its uuid, or for a zone-level read the facility zone it named.
function readPlacement(
  exception: YardException,
  available: AvailableLocations | null
): AddTrailerLocation | null {
  if (!available) return null;
  const uuid = exception.spotUuid;
  if (uuid && available.dockDoors.some((door) => door.dockUuid === uuid)) {
    return { type: 'dock', uuid };
  }
  if (uuid && available.yardSpots.some((spot) => spot.yard_spot_uuid === uuid)) {
    return { type: 'yard', uuid };
  }
  const zone = available.facilityZones.find(
    (z) => z.facility_zone_name === exception.rtlsZoneName
  );
  return zone ? { type: 'facility_zone', uuid: zone.facility_zone_uuid } : null;
}

// The place the person picked in the Add Trailer modal, as "<zone> - <dock or
// spot>", for the Possible Match dialog to set beside the match's.
function placementName(
  location: AddTrailerLocation,
  available: AvailableLocations | null
): string | null {
  if (!available) return null;
  switch (location.type) {
    case 'dock': {
      const door = available.dockDoors.find((d) => d.dockUuid === location.uuid);
      return zonedPlaceLabel(door?.zoneName, door?.dockName);
    }
    case 'yard': {
      const spot = available.yardSpots.find((s) => s.yard_spot_uuid === location.uuid);
      return zonedPlaceLabel(spot?.zoneName, spot?.yard_spot_name);
    }
    case 'facility_zone': {
      const zone = available.facilityZones.find(
        (z) => z.facility_zone_uuid === location.uuid
      );
      return zonedPlaceLabel(zone?.facility_zone_name, null);
    }
  }
}

// What the person entered, waiting on their answer to flux's possible matches.
interface MatchPrompt {
  exception: YardException;
  values: AddTrailerValues;
  matches: GhostTrailerMatch[];
}

// A held correction already created the trailer; creating again retries the
// placement, reusing that trailer.
const createTrailerLabel = (exception: YardException) =>
  exception.heldCorrection ? 'Retry' : 'Create trailer';

// As much of the read's location as is known — "<zone> - <spot>", just the
// zone for a zone-level read — falling back to the generic label (e.g.
// "Yard-level read") only when there's neither. Shared by the card and the
// detail dialog so both are titled the same.
function readLocation(exception: YardException, locations: FacilityLocations | null) {
  const spot = findLocation(locations, exception.spotUuid);
  const zone = spot?.zone ?? exception.rtlsZoneName;
  const name = spot?.name ?? exception.rtlsSpotName;
  return {
    zone,
    name,
    title: [zone, name].filter(Boolean).join(' - ') || exception.spot
  };
}

const LocationColumn = ({
  title,
  zone,
  spot,
  spotLabel = 'Spot',
  trailer
}: {
  title: string;
  zone: string;
  spot: string;
  /** "Dock" when `spot` is a dock door rather than a yard spot. */
  spotLabel?: string;
  trailer: string;
}) => (
  <Box sx={{ flex: 1 }}>
    <Typography
      variant="overline"
      sx={{ color: 'text.secondary', fontWeight: 500, letterSpacing: '.06em' }}
    >
      {title}
    </Typography>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
      <Box>
        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Zone</Typography>
        <Typography sx={{ fontWeight: 500 }}>{zone}</Typography>
      </Box>
      <Box>
        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
          {spotLabel}
        </Typography>
        <Typography sx={{ fontWeight: 500 }}>{spot}</Typography>
      </Box>
      <Box>
        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Trailer</Typography>
        <Typography sx={{ fontFamily: MONO, fontWeight: 500 }}>{trailer}</Typography>
      </Box>
    </Box>
  </Box>
);

const LegendSwatch = ({ color, label }: { color: string; label: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
    <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: color }} />
    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
      {label}
    </Typography>
  </Box>
);

const StatTile = ({
  value,
  label,
  color
}: {
  value: string;
  label: string;
  color?: string;
}) => (
  <Paper sx={{ px: 2.5, py: 1.5, minWidth: 118 }}>
    <Typography sx={{ fontSize: 28, lineHeight: 1.2, color }}>{value}</Typography>
    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
      {label}
    </Typography>
  </Paper>
);

// Opens the exception's detail dialog, which is where it is acted on.
const ExceptionCard = ({
  exception,
  locations,
  onOpen
}: {
  exception: YardException;
  locations: FacilityLocations | null;
  onOpen: () => void;
}) => {
  // A card of a type this page does not know yet still shows, generically.
  const Icon = exception.type ? TYPE_ICONS[exception.type] : ErrorOutline;
  const accent = SEVERITY_COLOR[exception.severity];
  return (
    <Paper
      onClick={onOpen}
      sx={{
        'cursor': 'pointer',
        'overflow': 'hidden',
        'transition': 'box-shadow .2s',
        '&:hover': { boxShadow: 6 }
      }}
    >
      <Box sx={{ height: 4, bgcolor: accent }} />
      <Box sx={{ p: 2, pb: 1 }}>
        <Typography sx={{ fontSize: 20, fontWeight: 500, lineHeight: 1.2 }}>
          {readLocation(exception, locations).title}
        </Typography>
      </Box>
      <Box sx={{ px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icon sx={{ fontSize: 20, color: accent }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {exception.type ? EXCEPTION_TYPE_LABELS[exception.type] : 'Exception'}
          </Typography>
        </Box>
        <Box
          sx={{ display: 'flex', border: '1px solid rgba(0,0,0,.12)', borderRadius: 1 }}
        >
          {[
            ['Device sees', exception.seen],
            ['System says', exception.expected]
          ].map(([label, value], i) => (
            <Box
              key={label}
              sx={{
                flex: 1,
                px: 1.5,
                py: 1,
                borderLeft: i ? '1px solid rgba(0,0,0,.12)' : 0
              }}
            >
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                {label}
              </Typography>
              <Typography sx={{ fontFamily: MONO, fontSize: 15, fontWeight: 500 }}>
                {value}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            color: 'text.secondary',
            fontSize: 12
          }}
        >
          <span>Match confidence {exception.confidence}</span>
          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Schedule sx={{ fontSize: 14 }} />
            {exception.age}
          </Box>
        </Box>
        {exception.heldCorrection && (
          <Typography variant="body2" sx={{ color: '#b26a00' }}>
            {exception.heldCorrection}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

// One empty list, so a closed Possible Match dialog's effect doesn't rerun.
const NO_MATCHES: GhostTrailerMatch[] = [];

const DIVIDER = '1px solid rgba(0,0,0,.12)';

// Fullscreen view of the camera still over a dark backdrop; clicking anywhere
// (or Esc) closes it.
const PhotoLightbox = ({
  src,
  open,
  onClose
}: {
  src: string;
  open: boolean;
  onClose: () => void;
}) => (
  <Modal
    open={open}
    onClose={onClose}
    slotProps={{ backdrop: { sx: { bgcolor: 'rgba(0,0,0,.9)' } } }}
  >
    <Box
      onClick={onClose}
      sx={{
        position: 'fixed',
        inset: 0,
        p: 4,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'zoom-out',
        outline: 'none'
      }}
    >
      <IconButton
        aria-label="Close photo"
        onClick={onClose}
        sx={{ position: 'absolute', top: 8, right: 8, color: 'common.white' }}
      >
        <Close />
      </IconButton>
      <Box
        component="img"
        src={src}
        alt="Camera still"
        sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
      />
    </Box>
  </Modal>
);

const ExceptionDetailDialog = ({
  exception,
  geometry,
  locations,
  onClose,
  onIgnore,
  ignoring,
  onCreateTrailer
}: {
  exception: YardException | null;
  /** Only for drawing the map; names come from `locations`. */
  geometry: GeoJSON.FeatureCollection<GeoJSON.Geometry, YardGeometryProps> | null;
  locations: FacilityLocations | null;
  onClose: () => void;
  onIgnore: () => void;
  ignoring: boolean;
  /** Shown only when given — i.e. for an exception that can create its trailer. */
  onCreateTrailer?: () => void;
}) => {
  const rtls = exception ? readLocation(exception, locations) : null;
  // Ziggy's report page is an internal tool, so only FreightRoll staff get the link.
  const { user } = useContext(UserContext) as { user?: User | null };
  const rtlsReportUrl = isFreightrollUser(user) ? exception?.debugUrl : null;
  const [photoOpen, setPhotoOpen] = useState(false);

  return (
    <Dialog open={Boolean(exception)} onClose={onClose} maxWidth="md" fullWidth>
      {exception && (
        <>
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 24, lineHeight: 1.2 }}>
                {rtls?.title}
              </Typography>
              {exception.heldCorrection && (
                <Typography variant="body2" sx={{ color: '#b26a00', mt: 0.5 }}>
                  {exception.heldCorrection}
                </Typography>
              )}
            </Box>
            <IconButton onClick={onClose} aria-label="Close">
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 0 }}>
            {exception.explanation && (
              <Box sx={{ px: 3, py: 2, borderBottom: DIVIDER }}>
                <Typography
                  variant="overline"
                  sx={{ color: 'primary.main', fontWeight: 500, letterSpacing: '.06em' }}
                >
                  Explanation
                </Typography>
                <Typography variant="body2">{exception.explanation}</Typography>
              </Box>
            )}
            <Box
              sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}
            >
              {/* Upper left: YMS vs RTLS location */}
              <Box sx={{ p: 3, borderBottom: DIVIDER, borderRight: { md: DIVIDER } }}>
                <Typography
                  variant="overline"
                  sx={{ color: 'primary.main', fontWeight: 500, letterSpacing: '.06em' }}
                >
                  Location
                </Typography>
                <Box sx={{ display: 'flex', gap: 4, mt: 1 }}>
                  <LocationColumn
                    title="YMS (Expected)"
                    zone={exception.ymsZoneName ?? NO_SPOT}
                    spot={exception.ymsSpotName ?? NO_SPOT}
                    spotLabel={exception.ymsAtDock ? 'Dock' : 'Spot'}
                    trailer={exception.ymsTrailer ?? NO_SPOT}
                  />
                  <LocationColumn
                    title="RTLS (Measured)"
                    zone={rtls?.zone ?? NO_SPOT}
                    spot={rtls?.name ?? NO_SPOT}
                    trailer={exception.ocrReading ?? NO_SPOT}
                  />
                </Box>
              </Box>

              {/* Upper right: last image */}
              <Box sx={{ p: 3, borderBottom: DIVIDER }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Last image
                </Typography>
                <Box
                  sx={{
                    mt: 1,
                    aspectRatio: '4/3',
                    border: '1px solid rgba(0,0,0,.12)',
                    borderRadius: 1,
                    bgcolor: '#eee',
                    color: 'rgba(0,0,0,.38)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden'
                  }}
                >
                  {exception.imageUrl ? (
                    <>
                      <Box
                        component="img"
                        src={exception.imageUrl}
                        alt="Camera still"
                        onClick={() => setPhotoOpen(true)}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          cursor: 'zoom-in'
                        }}
                      />
                      <PhotoLightbox
                        src={exception.fullImageUrl ?? exception.imageUrl}
                        open={photoOpen}
                        onClose={() => setPhotoOpen(false)}
                      />
                    </>
                  ) : (
                    <>
                      <PhotoCamera sx={{ fontSize: 34 }} />
                      <Typography variant="caption">Camera still</Typography>
                    </>
                  )}
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {exception.device}
                </Typography>
              </Box>

              {/* Lower left: current read details */}
              <Box
                sx={{
                  p: 3,
                  borderBottom: { xs: DIVIDER, md: 0 },
                  borderRight: { md: DIVIDER }
                }}
              >
                <Typography
                  variant="overline"
                  sx={{ color: 'primary.main', fontWeight: 500, letterSpacing: '.06em' }}
                >
                  Current read
                </Typography>
                {exception.readDetails.map((f) => (
                  <Box
                    key={f.label}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 2,
                      py: 1,
                      borderBottom: '1px solid rgba(0,0,0,.08)'
                    }}
                  >
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {f.label}
                    </Typography>
                    {f.tag ? (
                      <Chip
                        size="small"
                        label={f.value}
                        sx={{ bgcolor: 'rgba(0,0,0,.08)' }}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {f.value}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>

              {/* Lower right: map */}
              <Box sx={{ p: 3 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Location
                </Typography>
                <Box
                  sx={{
                    mt: 1,
                    aspectRatio: '4/3',
                    border: '1px solid rgba(0,0,0,.12)',
                    borderRadius: 1,
                    bgcolor: 'grey.50',
                    overflow: 'hidden'
                  }}
                >
                  <YardSpotMap
                    lat={exception.reading?.lat ?? null}
                    lon={exception.reading?.lon ?? null}
                    heading={exception.reading?.heading ?? null}
                    geometry={geometry}
                    spotUuid={exception.spotUuid}
                    spotName={exception.spot}
                    zoneName={null}
                    expectedSpotUuid={exception.expectedSpotUuid}
                  />
                </Box>
                {exception.expectedSpotUuid &&
                  exception.expectedSpotUuid !== exception.spotUuid && (
                    <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                      <LegendSwatch color="#22c55e" label="Read here" />
                      <LegendSwatch color="#f59e0b" label="Expected here" />
                    </Box>
                  )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 1.5 }}>
            {/* Only a currently-open exception has an exceptionId — a row opened
                from the audit log that isn't one (a past move, an already-ignored
                or already-resolved hold) gets no Ignore button. */}
            {exception.exceptionId && (
              <Button
                variant="contained"
                color="error"
                disabled={ignoring}
                onClick={onIgnore}
                sx={{ mr: 'auto' }}
              >
                {ignoring ? 'Ignoring…' : 'Ignore'}
              </Button>
            )}
            {rtlsReportUrl && (
              <Button
                variant="contained"
                sx={BLUE_BUTTON_SX}
                href={rtlsReportUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in RTLS
              </Button>
            )}
            <Button variant="contained" sx={BLUE_BUTTON_SX} onClick={onClose}>
              Close
            </Button>
            {onCreateTrailer && (
              <Button variant="contained" sx={BLUE_BUTTON_SX} onClick={onCreateTrailer}>
                {createTrailerLabel(exception)}
              </Button>
            )}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

const AuditLog = ({
  entries,
  onOpen,
  onUndo
}: {
  entries: AuditLogEntry[];
  onOpen: (entry: AuditLogEntry) => void;
  onUndo: (entry: AuditLogEntry) => void;
}) => {
  const [filter, setFilter] = useState<LogFilter>('all');
  const [trailerQuery, setTrailerQuery] = useState('');
  const [suggestedFilter, setSuggestedFilter] = useState<AuditStep[]>([]);
  const [executedFilter, setExecutedFilter] = useState<ExecutedStep[]>([]);
  // Filtered here rather than through the grid's filter model, which (outside
  // DataGrid Pro) holds only one filter at a time.
  const query = trailerQuery.trim().toLowerCase();
  const visible = entries.filter(
    (e) =>
      (filter === 'all' || (filter === 'auto' ? e.automatic : !e.automatic)) &&
      passes(suggestedFilter, e.suggested) &&
      passes(executedFilter, e.executed) &&
      (!query || e.trailer.toLowerCase().includes(query))
  );

  const columns = useMemo<GridColDef<AuditLogEntry>[]>(
    () => [
      {
        field: 'insertedAt',
        headerName: 'Timestamp',
        width: 165,
        valueGetter: (_value, row) => new Date(row.insertedAt),
        renderCell: (params: GridRenderCellParams<AuditLogEntry>) => (
          <Box sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
            {params.row.time}
          </Box>
        )
      },
      {
        field: 'trailer',
        headerName: 'Trailer #',
        width: 150,
        filterable: false,
        disableColumnMenu: true,
        headerClassName: 'header-control',
        renderHeader: () => (
          <HeaderControl>
            <TextField
              fullWidth
              variant="standard"
              size="small"
              placeholder="Trailer #"
              value={trailerQuery}
              onChange={(e) => setTrailerQuery(e.target.value)}
              inputProps={{ 'aria-label': 'Search trailers' }}
              InputProps={{
                sx: { fontSize: 13 },
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 16 }} />
                  </InputAdornment>
                ),
                endAdornment: trailerQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Clear trailer search"
                      onClick={() => setTrailerQuery('')}
                      sx={{ p: 0.25 }}
                    >
                      <Close sx={{ fontSize: 14 }} />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </HeaderControl>
        ),
        renderCell: (params: GridRenderCellParams<AuditLogEntry>) => (
          <Box sx={{ fontFamily: MONO, fontSize: 13 }}>{params.row.trailer}</Box>
        )
      },
      {
        field: 'suggestedSpot',
        headerName: 'Suggested Spot (RTLS)',
        width: 130,
        sortable: false,
        renderHeader: () => <PlainHeader title="Suggested Spot" subtitle="(RTLS)" />,
        renderCell: (params: GridRenderCellParams<AuditLogEntry>) => (
          <SpotCell spot={params.row.suggestedSpot} />
        )
      },
      {
        field: 'expectedSpot',
        headerName: 'Expected Spot (YMS)',
        width: 115,
        sortable: false,
        renderHeader: () => <PlainHeader title="Expected Spot" subtitle="(YMS)" />,
        renderCell: (params: GridRenderCellParams<AuditLogEntry>) => (
          <SpotCell spot={params.row.expectedSpot} />
        )
      },
      {
        field: 'suggested',
        headerName: 'Suggested',
        width: 140,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        headerClassName: 'header-control',
        renderHeader: () => (
          <StepFilter
            label="Suggested"
            steps={SUGGESTED_STEPS}
            value={suggestedFilter}
            onChange={setSuggestedFilter}
          />
        ),
        renderCell: (params: GridRenderCellParams<AuditLogEntry>) =>
          params.row.suggested ? (
            <StepChip step={params.row.suggested} />
          ) : (
            <Box sx={{ color: 'text.secondary' }}>—</Box>
          )
      },
      {
        field: 'executed',
        headerName: 'Executed',
        width: 140,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        headerClassName: 'header-control',
        renderHeader: () => (
          <StepFilter
            label="Executed"
            steps={EXECUTED_STEPS}
            value={executedFilter}
            onChange={setExecutedFilter}
          />
        ),
        renderCell: (params: GridRenderCellParams<AuditLogEntry>) => (
          <StepChip step={params.row.executed} />
        )
      },
      {
        field: 'explanation',
        headerName: 'Explanation',
        flex: 2,
        minWidth: 220,
        sortable: false,
        // One line in the table; the whole of it on hover, and in the row's dialog.
        renderCell: (params: GridRenderCellParams<AuditLogEntry>) => (
          <Box
            title={params.row.explanation}
            sx={{
              fontSize: 13,
              color: 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {params.row.explanation}
          </Box>
        )
      },
      {
        field: 'actor',
        headerName: 'Actor',
        width: 170,
        sortable: false,
        renderCell: (params: GridRenderCellParams<AuditLogEntry>) =>
          params.row.manual ? (
            // requestedBy is a public user id, not a name — keep it to the
            // tooltip and show what kind of correction it was instead.
            <Chip
              size="small"
              title={`Requested by ${params.row.actor}`}
              label={`Manual · ${params.row.correction ?? 'correction'}`}
              sx={{ bgcolor: '#fff4e5', color: '#b26a00', border: '1px solid #ffcc80' }}
            />
          ) : params.row.automatic ? (
            <Chip
              size="small"
              label="System · auto"
              sx={{ bgcolor: '#ede7f6', color: '#4527a0', border: '1px solid #b39ddb' }}
            />
          ) : (
            params.row.actor
          )
      },
      {
        field: 'actions',
        headerName: '',
        width: 90,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params: GridRenderCellParams<AuditLogEntry>) => (
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%'
            }}
          >
            <Button
              size="small"
              variant="contained"
              sx={BLUE_BUTTON_SX}
              onClick={() => onOpen(params.row)}
            >
              Open
            </Button>
            {params.row.canUndo && (
              <Button
                size="small"
                variant="contained"
                sx={BLUE_BUTTON_SX}
                onClick={() => onUndo(params.row)}
              >
                Undo
              </Button>
            )}
          </Box>
        )
      }
    ],
    [onOpen, onUndo, trailerQuery, suggestedFilter, executedFilter]
  );

  return (
    <Paper sx={{ overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2.5,
          flexWrap: 'wrap',
          px: 3,
          py: 2
        }}
      >
        <Box>
          <Typography variant="h6">Audit log</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Every reconciliation action, including moves the system resolved on its own.
          </Typography>
        </Box>
        <ToggleButtonGroup
          size="small"
          color="primary"
          exclusive
          value={filter}
          onChange={(_, v: LogFilter | null) => v && setFilter(v)}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="manual">People</ToggleButton>
          <ToggleButton value="auto">Automatic</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <DataGrid
        rows={visible}
        columns={columns}
        density="compact"
        disableRowSelectionOnClick
        // No column menu yet: nothing on it is needed, and its button squeezes
        // the two-line headers.
        disableColumnMenu
        autoHeight
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
          sorting: { sortModel: [{ field: 'insertedAt', sort: 'desc' }] }
        }}
        pageSizeOptions={[25, 50, 100]}
        getRowClassName={(params) =>
          params.row.manual ? 'manual-row' : params.row.automatic ? 'auto-row' : ''
        }
        sx={{
          'border': 0,
          '& .MuiDataGrid-columnHeaders': { bgcolor: '#fafafa' },
          // Let a header's input fill its column instead of shrinking to fit.
          '& .header-control .MuiDataGrid-columnHeaderTitleContainerContent': {
            flex: 1
          },
          // macOS overlays scrollbars and hides them until you scroll, so a table
          // wider than the screen gives no sign it scrolls. Styling the scrollbar
          // makes the browser draw a classic one that is always there.
          '& .MuiDataGrid-scrollbar--horizontal': {
            '&::-webkit-scrollbar': { height: 10 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'rgba(0,0,0,.06)' },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,.35)',
              borderRadius: 5
            }
          },
          '& .auto-row': { bgcolor: '#f5f9ff' },
          '& .manual-row': { bgcolor: '#fffaf2' }
        }}
      />
    </Paper>
  );
};

interface YardChecksExceptionsProps {
  selectedFacility: Facility | null;
  geometry: GeoJSON.FeatureCollection<GeoJSON.Geometry, YardGeometryProps> | null;
}

interface ExceptionsData {
  exceptions: YardException[];
  openCount: number;
  autoResolved: number;
  /**
   * Every row from either query, mapped to the same detail view the cards
   * use — so the audit log's "Open" button can show any row's modal, not
   * just the ones that are still open exceptions.
   */
  detailsById: Map<string, YardException>;
}

const YardChecksExceptions = ({
  selectedFacility,
  geometry
}: YardChecksExceptionsProps) => {
  const [data, setData] = useState<ExceptionsData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resolved, setResolved] = useState<Set<string>>(new Set());
  const [ignoringIds, setIgnoringIds] = useState<Set<string>>(new Set());
  const [log, setLog] = useState<AuditLogEntry[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [creatingFor, setCreatingFor] = useState<YardException | null>(null);
  const [hours, setHours] = useState<TimeWindowHours>(1);
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState<FacilityLocations | null>(null);
  // The docks, spots and zones the Add Trailer modal offers, loaded when it
  // opens, as on the trailers page.
  const [placements, setPlacements] = useState<AvailableLocations | null>(null);
  const [matchPrompt, setMatchPrompt] = useState<MatchPrompt | null>(null);
  const [matchBusy, setMatchBusy] = useState<GhostTrailerMatchAction | null>(null);
  const [matchError, setMatchError] = useState<string | null>(null);

  const facilityId = selectedFacility?.id;
  const windowLabel = TIME_WINDOWS.find((w) => w.hours === hours)!.label;

  // Spot, dock and zone names for the facility. The page works without them —
  // each place falls back to what its own row carries — so a failure only
  // costs those names, not the page.
  useEffect(() => {
    setLocations(null);
    if (facilityId === undefined) return;
    let cancelled = false;
    loadFacilityLocations(facilityId)
      .then((loaded) => {
        if (!cancelled) setLocations(loaded);
      })
      .catch((err) => console.error('Failed to load facility locations', err));
    return () => {
      cancelled = true;
    };
  }, [facilityId]);

  useEffect(() => {
    if (!creatingFor || facilityId === undefined) return;
    let cancelled = false;
    setPlacements(null);
    loadAvailableLocations(String(facilityId))
      .then((loaded) => {
        if (!cancelled) setPlacements(loaded);
      })
      .catch((err) => {
        console.error('Failed to load available locations', err);
        // Stop the spinner; the picker then shows that nothing is available.
        if (!cancelled)
          setPlacements({ dockDoors: [], yardSpots: [], facilityZones: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [creatingFor, facilityId]);

  // A different facility's data shouldn't linger while the new one loads;
  // a different time window's can (it's the same facility, just a narrower or
  // wider slice), so only this clears it.
  useEffect(() => {
    setData(null);
  }, [facilityId]);

  const load = useCallback(async () => {
    if (facilityId === undefined) return;
    setError(null);
    setLoading(true);
    setOpenId(null);
    try {
      const [exceptionsResult, auditRows] = await Promise.all([
        fetchYardObservationExceptions(facilityId, hours),
        fetchYardObservationAuditLog(facilityId, hours)
      ]);
      const now = new Date();

      // The two queries can overlap (an open hold shows up in both); collect
      // by id first so every row — audit-only or exception-only — is mapped
      // exactly once.
      const rowsById = new Map<string, YardObservationDecision>();
      for (const row of auditRows) rowsById.set(row.id, row);
      for (const row of exceptionsResult.exceptions) rowsById.set(row.id, row);
      const detailsById = new Map<string, YardException>();
      rowsById.forEach((row, id) => detailsById.set(id, toYardException(row, now)));

      setData({
        exceptions: exceptionsResult.exceptions.map((row) => detailsById.get(row.id)!),
        openCount: exceptionsResult.openCount,
        autoResolved: countAutoResolved(auditRows, now, hours),
        detailsById
      });
      setLog(toAuditEntries(auditRows, now));
      setResolved(new Set());
      setIgnoringIds(new Set());
    } catch (err: any) {
      setError(err.message || 'Failed to load yard exceptions');
    } finally {
      setLoading(false);
    }
  }, [facilityId, hours]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded mb-4">
        {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="flex justify-center items-center py-16">
        <CircularProgress />
      </div>
    );
  }

  // Cards are the exceptions themselves, so they are keyed — and resolved — by
  // the exception's id, which holds steady while newer reads replace the
  // decision row a card is built from.
  const cardKey = (e: YardException) => e.exceptionId ?? e.id;
  const openList = data.exceptions.filter((e) => !resolved.has(cardKey(e)));
  const detail = (openId && data.detailsById.get(openId)) || null;

  // Returns whether it succeeded, so the detail dialog (which also wants to
  // close itself, but only once the ignore actually goes through) can tell.
  const handleIgnore = async (exception: YardException): Promise<boolean> => {
    // Every card comes from the exceptions query, so this is never null in
    // practice — the guard is just so a stray audit-only row can't be sent.
    if (!exception.exceptionId) return false;
    const key = cardKey(exception);
    setIgnoringIds((prev) => new Set(prev).add(key));
    try {
      await ignoreException(exception.exceptionId);
      setResolved((prev) => new Set(prev).add(key));
      setToast(`${readLocation(exception, locations).title} — exception ignored.`);
      return true;
    } catch (err: any) {
      setToast(err.message || 'Failed to ignore exception');
      return false;
    } finally {
      setIgnoringIds((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const submitTrailer = (
    exception: YardException,
    { scac, trailerNumber, status, notes, location }: AddTrailerValues,
    ignoreMatches: boolean
  ) =>
    createUnknownTrailer({
      exceptionId: exception.exceptionId!,
      scac,
      trailerNumber,
      loadStatus: status.toLowerCase() as TrailerLoadStatus,
      location: {
        // The modal calls a yard spot `yard`; flux calls it `yard_spot`.
        type: location.type === 'yard' ? 'yard_spot' : location.type,
        uuid: location.uuid
      },
      notes,
      ignoreMatches
    });

  // Throws for an outcome that should stay in the modal (a GraphQL refusal, or
  // the engine erroring) so AddTrailerModal shows it. Possible matches leave it
  // open behind the Possible Match dialog; anything else closes it.
  const handleCreateTrailer = async (
    exception: YardException,
    values: AddTrailerValues
  ) => {
    const result = await submitTrailer(exception, values, false);
    if (result.possibleMatches.length > 0) {
      setMatchError(null);
      setMatchPrompt({ exception, values, matches: result.possibleMatches });
      return;
    }
    applyCreateResult(exception, result, values.trailerNumber);
  };

  const applyCreateResult = (
    exception: YardException,
    result: CreateUnknownTrailerResult,
    trailerNumber: string
  ) => {
    const outcome = createTrailerOutcome(result, trailerNumber);
    if (outcome.kind === 'error') throw new Error(outcome.message);

    setCreatingFor(null);
    setToast(outcome.message);
    if (outcome.kind === 'resolved') {
      setResolved((prev) => new Set(prev).add(cardKey(exception)));
      setOpenId(null);
    } else if (outcome.kind === 'held') {
      // The hold is a new decision on the same exception — reload so the card
      // shows it (as "Trailer created, not placed"). Retrying reuses the
      // trailer, so no duplicate is made.
      void load();
    }
  };

  const closeMatchPrompt = () => {
    setMatchPrompt(null);
    setMatchError(null);
  };

  // Runs one answer to the Possible Match dialog, keeping it open with the
  // error when flux refuses.
  const answerMatchPrompt = async (
    action: GhostTrailerMatchAction,
    answer: (prompt: MatchPrompt) => Promise<void>
  ) => {
    if (!matchPrompt) return;
    setMatchBusy(action);
    setMatchError(null);
    try {
      await answer(matchPrompt);
      closeMatchPrompt();
    } catch (err: any) {
      setMatchError(err.message || 'Something went wrong');
    } finally {
      setMatchBusy(null);
    }
  };

  // The match is the trailer: the exception resolves, and the trailer stays
  // where flux has it.
  const handleAcceptMatch = (match: GhostTrailerMatch) =>
    answerMatchPrompt('accept', async ({ exception }) => {
      await resolveGhostTrailerWithMatch(exception.exceptionId!, match.trailerUuid);
      setCreatingFor(null);
      setResolved((prev) => new Set(prev).add(cardKey(exception)));
      setOpenId(null);
      setToast(
        `${match.trailerNumber ?? 'Trailer'} is already on this campus — exception resolved.`
      );
    });

  // None of the matches is the trailer: create it as entered.
  const handleCreateDespiteMatch = () =>
    answerMatchPrompt('create', async ({ exception, values }) => {
      const result = await submitTrailer(exception, values, true);
      applyCreateResult(exception, result, values.trailerNumber);
    });

  // Nevermind: back out entirely, the exception as it was.
  const handleCancelMatch = () => {
    closeMatchPrompt();
    setCreatingFor(null);
  };

  const handleUndo = async (entry: AuditLogEntry) => {
    try {
      await undoAuditAction(entry.id);
      setLog((prev) =>
        prev.map((e) =>
          e.id === entry.id
            ? {
                ...e,
                action: 'Reverted',
                executed: executedStepOf('Reverted'),
                canUndo: false
              }
            : e
        )
      );
      setToast(`${entry.spot} — action reverted.`);
    } catch (err: any) {
      setToast(err.message || 'Undo failed');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 3,
          flexWrap: 'wrap'
        }}
      >
        <Box>
          <Typography variant="h5">Suggested New Trailers</Typography>
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', maxWidth: '60ch', mt: 0.5 }}
          >
            Spots where the device reads disagree with the yard system. Open a card to
            review the read, then create a move or ignore it.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 140 }} disabled={loading}>
            <InputLabel id="time-window-label">Time window</InputLabel>
            <Select
              labelId="time-window-label"
              label="Time window"
              value={hours}
              onChange={(e) => setHours(e.target.value as TimeWindowHours)}
            >
              {TIME_WINDOWS.map((w) => (
                <MenuItem key={w.hours} value={w.hours}>
                  {w.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <StatTile
            value={String(Math.max(data.openCount - resolved.size, 0))}
            label="Open exceptions"
            color="#d32f2f"
          />
          <StatTile
            value={String(data.autoResolved)}
            label={`Auto-resolved ${windowLabel}`}
          />
        </Box>
      </Box>

      {openList.length === 0 ? (
        <Typography sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
          No open exceptions.
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            overflowX: 'auto',
            pb: 1
          }}
        >
          {openList.map((e) => (
            <Box key={cardKey(e)} sx={{ flex: '0 0 304px' }}>
              <ExceptionCard
                exception={e}
                locations={locations}
                onOpen={() => setOpenId(e.id)}
              />
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ mt: 1 }}>
        <AuditLog
          entries={log}
          onOpen={(entry) => {
            // A correction opens the card it answers while that is still open,
            // so the person lands on the exception, not just their own row.
            const answered =
              entry.answersExceptionId &&
              data.exceptions.find(
                (e) =>
                  e.exceptionId === entry.answersExceptionId && !resolved.has(cardKey(e))
              );
            setOpenId(answered ? answered.id : entry.id);
          }}
          onUndo={handleUndo}
        />
      </Box>

      <ExceptionDetailDialog
        exception={detail}
        geometry={geometry}
        locations={locations}
        onClose={() => setOpenId(null)}
        onIgnore={async () => {
          if (!detail) return;
          const ok = await handleIgnore(detail);
          if (ok) setOpenId(null);
        }}
        ignoring={detail ? ignoringIds.has(cardKey(detail)) : false}
        onCreateTrailer={
          detail && canCreateTrailer(detail) ? () => setCreatingFor(detail) : undefined
        }
      />

      <AddTrailerModal
        open={Boolean(creatingFor)}
        onClose={() => setCreatingFor(null)}
        facilityId={String(facilityId)}
        initialTrailerNumber={creatingFor?.trailerNumber ?? creatingFor?.ocrReading ?? ''}
        initialScac={
          (creatingFor?.heldCorrection && creatingFor.scac) ||
          creatingFor?.knownScac ||
          ''
        }
        initialLocation={creatingFor ? readPlacement(creatingFor, placements) : null}
        loadingLocations={Boolean(creatingFor) && placements === null}
        availableDockDoors={placements?.dockDoors ?? []}
        availableYardSpots={placements?.yardSpots ?? []}
        availableFacilityZones={placements?.facilityZones ?? []}
        zones={(placements?.facilityZones ?? []).map((zone) => ({
          id: zone.facility_zone_uuid,
          name: zone.facility_zone_name,
          color: zone.configuration?.color || '#3B82F6'
        }))}
        onSubmit={(values) => handleCreateTrailer(creatingFor!, values)}
      />

      <GhostTrailerMatchDialog
        matches={matchPrompt?.matches ?? NO_MATCHES}
        entered={
          matchPrompt && {
            scac: matchPrompt.values.scac,
            trailerNumber: matchPrompt.values.trailerNumber,
            locationUuid: matchPrompt.values.location.uuid,
            locationName: placementName(matchPrompt.values.location, placements)
          }
        }
        busy={matchBusy}
        error={matchError}
        onAccept={handleAcceptMatch}
        onCreateNew={handleCreateDespiteMatch}
        onCancel={handleCancelMatch}
      />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      />
    </Box>
  );
};

export default YardChecksExceptions;
